import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { YearProvider } from './contexts/YearContext';
import Layout from './components/Layout';
import LeaderboardPage from './features/leaderboard/components/MascotRace';
import GamesPage from './features/games/pages/GiochiPage';
import GiocoDettagliPage from './features/games/pages/GiocoDettagliPage';
import CalendarPage from './features/calendar/pages/CalendarioPage';
import BorgoPollPage from './features/poll/BorgoPollPage';
import LoginPage from './features/editor/pages/LoginPage';
import EditorHomePage from './features/editor/pages/EditorHomePage';
import EditLeaderboardPage from './features/editor/pages/EditLeaderboardPage';
import LeaderboardOverviewView from './features/editor/pages/LeaderboardOverviewView';
import LeaderboardGameView from './features/editor/pages/LeaderboardGameView';
import EditGameStatusPage from './features/editor/pages/EditGameStatusPage';
import GameStatusListView from './features/editor/pages/GameStatusListView';
import GameStatusDetailView from './features/editor/pages/GameStatusDetailView';
import StatsPage from './features/editor/pages/StatsPage';
import RequireAuth from './features/editor/components/RequireAuth';
import ArcadeGamePage from './features/arcade/pages/ArcadeGamePage';
import DinoGamePage from './features/arcade/pages/DinoGamePage';
import ReactionGamePage from './features/arcade/pages/ReactionGamePage';
import SequenceGamePage from './features/arcade/pages/SequenceGamePage';
import FlappyGamePage from './features/arcade/pages/FlappyGamePage';
import MiniGamesPage from './features/arcade/pages/MiniGamesPage';
import ScontriniPage from './features/receipts/pages/ScontriniPage';
import './App.css';

// Component to handle backward compatibility redirects
const BackwardCompatRedirect: React.FC<{ type: string }> = ({ type }) => {
  const { year } = useParams<{ year: string }>();
  return <Navigate to={`/${year}/${type}`} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <YearProvider>
          <Routes>
            {/* Editor (authenticated, separate layout) */}
            <Route path="/edit/login" element={<LoginPage />} />
            <Route path="/edit" element={<RequireAuth />}>
              <Route index element={<EditorHomePage />} />
              <Route path="leaderboard" element={<EditLeaderboardPage />}>
                <Route index element={<LeaderboardOverviewView />} />
                <Route path=":gameId" element={<LeaderboardGameView />} />
              </Route>
              <Route path="games" element={<EditGameStatusPage />}>
                <Route index element={<GameStatusListView />} />
                <Route path=":gameId" element={<GameStatusDetailView />} />
              </Route>
              <Route path="stats" element={<StatsPage />} />
            </Route>

            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/classifica" replace />} />
              
              {/* Current year routes */}
              <Route path="classifica" element={<LeaderboardPage />} />
              <Route path="giochi" element={<GamesPage />} />
              <Route path="giochi/:gameId" element={<GiocoDettagliPage />} />
              <Route path="calendario" element={<CalendarPage />} />
              <Route path="gioco">
                <Route index element={<MiniGamesPage />} />
                <Route path="bros" element={<ArcadeGamePage />} />
                <Route path="dino" element={<DinoGamePage />} />
                <Route path="reazione" element={<ReactionGamePage />} />
                <Route path="sequenza" element={<SequenceGamePage />} />
                <Route path="flappy" element={<FlappyGamePage />} />
              </Route>

              {/* Borgo popularity poll — always current/live, no year variant */}
              <Route path="borgo-amato" element={<BorgoPollPage />} />

              {/* Receipt game (Taverna) — always current/live, no year variant */}
              <Route path="scontrini" element={<ScontriniPage />} />

              {/* Year-first routes */}
              <Route path=":year">
                <Route index element={<Navigate to="classifica" replace />} />
                <Route path="classifica" element={<LeaderboardPage />} />
                <Route path="giochi" element={<GamesPage />} />
                <Route path="giochi/:gameId" element={<GiocoDettagliPage />} />
                <Route path="calendario" element={<CalendarPage />} />
                <Route path="gioco">
                  <Route index element={<MiniGamesPage />} />
                  <Route path="bros" element={<ArcadeGamePage />} />
                  <Route path="dino" element={<DinoGamePage />} />
                  <Route path="reazione" element={<ReactionGamePage />} />
                  <Route path="sequenza" element={<SequenceGamePage />} />
                  <Route path="flappy" element={<FlappyGamePage />} />
                </Route>
              </Route>
              
              {/* Backward compatibility redirects */}
              <Route path="classifica/:year" element={<BackwardCompatRedirect type="classifica" />} />
              <Route path="giochi/:year/overview" element={<BackwardCompatRedirect type="giochi" />} />
              <Route path="calendario/:year" element={<BackwardCompatRedirect type="calendario" />} />
            </Route>
          </Routes>
        </YearProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
